import SpinningStar from "../celebrate/SpinningStar";
import React, { useState } from "react";
import { getAuth } from "firebase/auth";
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  increment,
  serverTimestamp,
} from "firebase/firestore";
import { resolveEducatorUid } from "../../utils/resolveEducatorUid";
import { awardFirstPlayCoins } from "../../services/coinService";

// progressKey / starLabel let the 🎤 Sing-along button reuse this exact
// player and reward flow while recording its own progress
// (`singalong`, `singalongQty`, `lastSingalongAt`) instead of the video's.
// Defaults keep the Video button byte-for-byte as it was.
const VideoTool = ({
  videoUrl,
  subjectKey,
  slug,
  onProgressUpdate,
  progressKey = "video",
  starLabel = "Video",
}) => {
  const qtyKey = `${progressKey}Qty`;
  const lastKey = `last${progressKey.charAt(0).toUpperCase()}${progressKey.slice(1)}At`;
  const [showPopup, setShowPopup] = useState(false);
  const db = getFirestore();

  // A) write to userData/{uid}
  const saveProgressToUserData = async (user, subjectKey, slug) => {
    const userDataRef = doc(db, "userData", user.uid);
    await setDoc(
      userDataRef,
      {
        [subjectKey]: {
          [slug]: {
            [progressKey]: true,
            [qtyKey]: increment(1),
            [lastKey]: serverTimestamp(),
          },
        },
      },
      { merge: true }
    );
  };

  // B) mirror to classroomProgress/{educatorUid}/subjects/{subjectKey}/students/{studentUid}
  //
  // This used to run a where("role","in",[...]) query against /users with a
  // hardcoded list that did not include `va-3rd-educator` or any other real
  // role, so it matched nothing and returned silently for almost every
  // teacher. See utils/resolveEducatorUid.js.
  const mirrorToEducator = async (studentUid, subjectKey, slug) => {
    const studentSnap = await getDoc(doc(db, "users", studentUid));
    const studentDoc = studentSnap.data() || {};
    const educatorCode = studentDoc.educatorCode;
    if (!educatorCode) return;

    const educatorUid = await resolveEducatorUid(db, educatorCode);
    if (!educatorUid) return;

    const mirrorRef = doc(
      db,
      "classroomProgress",
      educatorUid,
      "subjects",
      subjectKey,
      "students",
      studentUid
    );

    await setDoc(
      mirrorRef,
      {
        studentUid,
        studentName: `${studentDoc.firstName || ""} ${studentDoc.lastName || ""}`.trim(),
        firstName: studentDoc.firstName || "",
        lastName: studentDoc.lastName || "",
        avatarUrl: studentDoc.avatarUrl || "",
        educatorCode,
        slugs: {
          [slug]: {
            [progressKey]: true,
            [qtyKey]: increment(1),
            [lastKey]: serverTimestamp(),
          },
        },
        lastUpdated: serverTimestamp(),
      },
      { merge: true }
    );
  };

  const markProgress = async () => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user?.uid) return;

      await saveProgressToUserData(user, subjectKey, slug);
      await mirrorToEducator(user.uid, subjectKey, slug);

      // 🪙 First time finishing this topic's video / sing-along pays coins
      // (once per topic, decided by the server). Not awaited: the star and
      // the popup never wait on it, and a failure costs only the coins.
      awardFirstPlayCoins({ kind: progressKey, slug });

      if (onProgressUpdate) {
        await onProgressUpdate();
      }
    } catch (error) {
      console.error("Error marking progress:", error);
    }
  };

  const handleConfirm = async () => {
    await markProgress();
    setShowPopup(false);
  };

  return (
    <>
      <video
        width="100%"
        controls
        onEnded={() => setShowPopup(true)}
        style={{ borderRadius: "10px", boxShadow: "0 4px 12px rgba(0,0,0,0.3)" }}
        controlsList="nodownload noplaybackrate noremoteplayback"
        disablePictureInPicture
        onContextMenu={(e) => e.preventDefault()}
      >
        <source src={videoUrl} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {showPopup && (
        <div style={overlayStyle}>
          <div style={popupStyle}>
            <SpinningStar size={130} />
            <p style={{ marginBottom: 16, fontSize: "20px", fontWeight: 800 }}>
              Congratulations! You've earned your {starLabel} Star!
            </p>
            <button onClick={handleConfirm} style={buttonStyle}>Continue</button>
          </div>
        </div>
      )}
    </>
  );
};

const overlayStyle = {
  position: "fixed",
  top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.6)",
  zIndex: 1000,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const popupStyle = {
  background: "#fff",
  padding: "30px 20px",
  borderRadius: "12px",
  textAlign: "center",
  maxWidth: "300px",
  boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
};

const buttonStyle = {
  margin: "8px",
  padding: "10px 18px",
  backgroundColor: "#2196f3",
  color: "#fff",
  border: "none",
  borderRadius: "6px",
  fontSize: "14px",
  cursor: "pointer",
};

export default VideoTool;
